import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Eye, RefreshCw, RotateCcw, Search } from "lucide-react";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyTableRow, ListTableFrame, LIST_PAGE_BODY } from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { ModalError, ModalLoading, QueryErrorState } from "@/components/ui/States";
import {
  useDeletedRecords,
  useDeletedSnapshot,
  useDeletedTables,
  useRestoreRecord,
} from "@/api/hooks";
import { ApiClientError, getLocalizedError } from "@/lib/api";
import { formatDateTime, timeAgo } from "@/domain/datetime";
import { formatMoney, majorUnits } from "@/domain/money";
import { apiFetch } from "@/lib/api";
import { ExportButton } from "@/components/domain/ExportButton";
import type { DeletedRecordDto, Paginated } from "@hisense/shared";
import { useClampListPage, useListQueryState } from "@/lib/listQuery";

const RESOURCE_KEY: Record<string, string> = {
  customers: "customers",
  suppliers: "suppliers",
  products: "products",
  proforma_invoices: "proformaInvoices",
  invoices: "invoices",
  quotations: "quotations",
  purchase_orders: "purchaseOrders",
  packing_lists: "packingLists",
  delivery_notes: "deliveryNotes",
  sample_orders: "sampleOrders",
  sample_cards: "sampleCards",
  // Các bảng ERP (M7). Thiếu chúng ở đây thì kho lưu trữ hiện tên bảng thô kiểu
  // "purchase_requisitions" cho người dùng cuối, và không dịch sang ngôn ngữ nào.
  boms: "boms",
  routings: "routings",
  sales_orders: "salesOrders",
  purchase_requisitions: "purchaseRequisitions",
  warehouses: "warehouses",
  warehouse_bins: "warehouseBins",
  stock_moves: "stockMoves",
  work_orders: "workOrders",
  manufacturing_orders: "workOrders",
  manufacturing_resources: "productionResources",
  inspections: "inspections",
  gl_accounts: "glAccounts",
  journal_entries: "journalEntries",
  service_tickets: "serviceTickets",
  // Thêm 22/09/2026 — thiếu thì cột Loại hiện "production_quality_specs"… ở cả ba thứ tiếng.
  // DeletedRecordsPage.test.ts đối chiếu bảng này với mọi bảng mà API ghi vào kho lưu trữ.
  production_quality_specs: "qualitySpecs",
  mrp_runs: "mrpRuns",
  product_unit_profiles: "unitProfiles",
  pi_payments: "piPayments",
  sales_opportunities: "salesOpportunities",
  service_ticket_cases: "serviceCases",
  sourcing_cases: "sourcingCases",
  subcontract_orders: "subcontractOrders",
  // 11/10/2026 (v1.8): tệp bằng chứng trên PI, yêu cầu sản phẩm và ảnh yêu cầu của khách bị gỡ khỏi danh sách — giữ dấu vết ai gỡ, lúc nào.
  pi_attachments: "piAttachments",
  p_process_attachments: "pProcessAttachments",
  customer_requirements: "customerRequirements",
  customer_requirement_photos: "customerRequirementPhotos",
};

/**
 * Xem toàn bộ bản lưu.
 *
 * Bản lưu JSON là thứ DUY NHẤT còn lại của một bản ghi đã xoá — không mở được
 * nó thì kho lưu trữ chỉ để đếm. Máy chủ che giá vốn / nhà cung cấp cho vai trò
 * không có quyền và nói ra là đã che, chứ không im lặng cắt bớt.
 */
function SnapshotViewer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useDeletedSnapshot(id);
  if (isLoading) {
    return <ModalLoading title={t("archive.snapshotTitle")} closeLabel={t("common.close")} onClose={onClose} />;
  }
  if (error || !data) {
    return (
      <ModalError
        title={t("archive.snapshotTitle")}
        closeLabel={t("common.close")}
        onClose={onClose}
        message={t("archive.errorLoad")}
        onRetry={error ? () => void refetch() : undefined}
      />
    );
  }
  return (
    <Modal title={t("archive.snapshotTitle")} closeLabel={t("common.close")} onClose={onClose}>
      {data.redacted && (
        <p className="mb-2 rounded-sm border border-[color:var(--color-warn)]/30 bg-warn-tint px-3 py-2 text-xs text-ink-soft">
          {t("archive.snapshotRedacted")}
        </p>
      )}
      <pre className="max-h-[60vh] overflow-auto rounded-sm border border-line bg-sunken p-3 font-mono text-2xs leading-relaxed text-ink-soft">
        {JSON.stringify(data.snapshot, null, 2)}
      </pre>
    </Modal>
  );
}

/** Mã lỗi của luật danh tính sản phẩm (mã + Sub Ref) mà máy chủ trả khi khôi phục. */
const IDENTITY_CONFLICTS = new Set(["sub_ref_required", "duplicate_identity", "sub_ref_required_on_other"]);

interface IdentityConflict {
  id: string;
  label: string;
  productCode: string;
  subRef: string;
  message: string;
  conflictingProductId?: string;
}

/**
 * Lỗi khôi phục vì cặp mã + Sub Ref của sản phẩm đã xoá đã có sản phẩm khác dùng.
 * Máy chủ gửi kèm mã / Sub Ref đang xét (`restoreProductCode`, `restoreSubRef`)
 * để điền sẵn ô sửa; thiếu chúng thì không phải ca này.
 */
function identityConflict(err: unknown, id: string, label: string, t: TFunction): IdentityConflict | null {
  if (!(err instanceof ApiClientError) || !IDENTITY_CONFLICTS.has(err.code)) return null;
  const f = err.fields ?? {};
  if (f.restoreProductCode === undefined) return null;
  return {
    id,
    label,
    productCode: f.restoreProductCode,
    subRef: f.restoreSubRef ?? "",
    message: getLocalizedError(err, t, "archive.errorRestore"),
    conflictingProductId: f.conflictingProductId,
  };
}

/**
 * Khôi phục sản phẩm với mã / Sub Ref mới.
 *
 * Khách chốt 19/10/2026: sản phẩm đã xoá nhả chỗ mã + Sub Ref, nên lúc khôi phục
 * cặp đó có thể đã thuộc sản phẩm khác. Khi đó người dùng sửa mã / Sub Ref của
 * bản đang khôi phục ngay tại đây, không phải đi sửa bản kia. Sản phẩm đang dùng
 * cặp cũ giữ nguyên; liên kết mở nó ở thẻ mới nếu muốn sửa bên đó.
 */
function RestoreProductDialog({ conflict, onClose }: { conflict: IdentityConflict; onClose: () => void }) {
  const { t } = useTranslation();
  const restore = useRestoreRecord();
  const [productCode, setProductCode] = useState(conflict.productCode);
  const [subRef, setSubRef] = useState(conflict.subRef);
  const [problem, setProblem] = useState(conflict);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await restore.mutateAsync({
        id: conflict.id,
        identity: { productCode: productCode.trim(), subRef: subRef.trim() || null },
      });
      onClose();
    } catch (err) {
      const again = identityConflict(err, conflict.id, conflict.label, t);
      if (again) setProblem(again);
      else setError(getLocalizedError(err, t, "archive.errorRestore"));
    }
  };

  return (
    <Modal
      title={t("archive.restoreProductTitle")}
      eyebrow={conflict.label}
      closeLabel={t("common.cancel")}
      onClose={onClose}
      className="max-w-lg"
    >
      <div className="min-h-0 overflow-y-auto p-4">
        <form onSubmit={submit} className="space-y-4">
          <div
            role="alert"
            className="rounded-sm border border-[color:var(--color-warn)]/30 bg-warn-tint px-3 py-2 text-xs text-ink-soft"
          >
            <p>{problem.message}</p>
            {problem.conflictingProductId && (
              <Link
                to={`/products/${problem.conflictingProductId}`}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block font-medium text-brand underline"
              >
                {t("archive.openConflictingProduct")}
              </Link>
            )}
          </div>
          <p className="text-xs text-ink-soft">{t("archive.restoreProductHint")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor="restore-code">{t("products.code")}</FieldLabel>
              <Input
                id="restore-code"
                className="font-mono"
                value={productCode}
                maxLength={60}
                required
                autoFocus
                onChange={(e) => setProductCode(e.target.value)}
              />
            </div>
            <div>
              <FieldLabel htmlFor="restore-subref">{t("products.subRef")}</FieldLabel>
              <Input
                id="restore-subref"
                className="font-mono"
                value={subRef}
                maxLength={120}
                onChange={(e) => setSubRef(e.target.value)}
              />
            </div>
          </div>
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" size="sm" disabled={restore.isPending || !productCode.trim()}>
              <RotateCcw className="size-3.5" /> {t("common.restore")}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export function DeletedRecordsPage() {
  const { t, i18n } = useTranslation();
  const list = useListQueryState();
  const table = list.searchParams.get("table") ?? "";
  const [viewing, setViewing] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  // Khôi phục đưa một bản ghi đã xoá TRỞ LẠI mọi danh sách và mọi báo cáo. Bấm
  // nhầm giữa một bảng dài là chuyện có thật, nên hỏi lại — giống hệt đường xoá.
  const [confirming, setConfirming] = useState<{ id: string; label: string } | null>(null);
  const [fixing, setFixing] = useState<IdentityConflict | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useDeletedRecords({
    q: list.q.trim() || undefined,
    table: table || undefined,
    page: list.page,
    pageSize: list.pageSize,
  });
  const tables = useDeletedTables();
  useClampListPage(data?.total, list.clampPage);
  const restore = useRestoreRecord();
  const rows = data?.data ?? [];

  const label = (name: string) =>
    RESOURCE_KEY[name] ? t(`resourceTypes.${RESOURCE_KEY[name]}`) : name;

  const onRestore = async ({ id, label }: { id: string; label: string }) => {
    setConfirming(null);
    setRestoreError(null);
    try {
      await restore.mutateAsync({ id });
    } catch (err) {
      // Sản phẩm bị trùng mã + Sub Ref: mở hộp cho nhập mã / Sub Ref mới thay vì chỉ báo lỗi.
      const conflict = identityConflict(err, id, label, t);
      if (conflict) {
        setFixing(conflict);
        return;
      }
      // Khôi phục thất bại phải NÓI RA. Bản cũ nuốt lỗi, người dùng bấm xong
      // thấy dòng vẫn nằm đó và không hiểu vì sao.
      setRestoreError(getLocalizedError(err, t, "archive.errorRestore"));
    }
  };

  return (
    <PageBody className={LIST_PAGE_BODY}>
      {viewing && <SnapshotViewer id={viewing} onClose={() => setViewing(null)} />}
      <ConfirmDialog
        open={confirming !== null}
        title={confirming?.label ?? ""}
        description={t("archive.confirmRestore")}
        confirmLabel={t("common.restore")}
        cancelLabel={t("common.cancel")}
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && void onRestore(confirming)}
      />
      {fixing && <RestoreProductDialog key={fixing.id} conflict={fixing} onClose={() => setFixing(null)} />}

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          className="w-80 max-w-full"
          leading={<Search />}
          placeholder={t("archive.searchPlaceholder")}
          {...list.searchInput}
        />
        {/* Kho lưu trữ thay đổi khi NGƯỜI KHÁC xoá bản ghi; không có nút này
            thì phải tải lại cả trang mới thấy. */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void refetch()}
          disabled={isFetching}
          aria-label={t("common.refresh")}
          title={t("common.refresh")}
        >
          <RefreshCw className={"size-3.5" + (isFetching ? " animate-spin" : "")} />
        </Button>
        <Select
          containerClassName="w-56"
          value={table}
          onChange={(e) => list.updateParam("table", e.target.value || null)}
          aria-label={t("common.type")}
        >
          <option value="">{t("archive.allTypes")}</option>
          {(tables.data ?? []).map((x) => (
            <option key={x.table} value={x.table}>
              {label(x.table)} ({x.count})
            </option>
          ))}
        </Select>
        {/*
          Sổ này là VẾT KIỂM TOÁN, nên thời điểm xoá xuất ra dạng TUYỆT ĐỐI, không
          phải "3 ngày trước" như trên màn hình — chuỗi tương đối không đối chiếu
          được với bất cứ thứ gì bên ngoài.
        */}
        <div className="ml-auto">
          <ExportButton<DeletedRecordDto>
            name="deleted-records"
            title={t("nav.deletedRecords")}
            rows={rows}
            total={data?.total}
            disabled={isFetching}
            errorKey="archive.errorLoad"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<DeletedRecordDto>>("/deleted-records", {
                query: { q: list.q.trim() || undefined, table: table || undefined, page, pageSize },
                signal,
              })
            }
            columns={[
              { header: t("common.type"), value: (r) => label(r.originalTable) },
              { header: t("archive.documentNo"), value: (r) => r.documentNo ?? r.originalId },
              { header: t("archive.party"), value: (r) => r.partyName },
              { header: t("pi.poNo"), value: (r) => r.customerPoNo },
              { header: t("common.total"), value: (r) => (r.totalAmount == null ? null : majorUnits(r.totalAmount)) },
              { header: t("common.currency"), value: (r) => r.currency },
              { header: t("common.deletedBy"), value: (r) => r.deletedByName },
              { header: t("common.when"), value: (r) => formatDateTime(r.deletedAt, i18n.language) },
            ]}
          />
        </div>
      </div>

      {restoreError && (
        <p className="mt-2 shrink-0 rounded-sm border border-[color:var(--color-danger)]/30 bg-danger-tint px-3 py-2 text-xs text-danger">
          {restoreError}
        </p>
      )}

      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("archive.errorLoad")}
        // 403 không phải "không tải được": dữ liệu vẫn ở đó, chỉ là vai trò này
        // không được xem — và nút Thử lại chỉ gọi lại đúng route đó để nhận
        // đúng 403 đó.
        errorNode={
          <QueryErrorState
            error={error}
            message={t("archive.errorLoad")}
            forbiddenMessage={t("errors.forbidden")}
            onRetry={() => refetch()}
          />
        }
        page={data?.page ?? list.page}
        pageSize={data?.pageSize ?? list.pageSize}
        total={data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th>{t("common.type")}</Th>
            <Th>{t("archive.documentNo")}</Th>
            <Th>{t("archive.party")}</Th>
            <Th>{t("pi.poNo")}</Th>
            <Th align="right">{t("common.total")}</Th>
            <Th>{t("common.deletedBy")}</Th>
            <Th>{t("common.when")}</Th>
            <Th align="right">{t("common.actions")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyTableRow colSpan={8}>{t("archive.empty")}</EmptyTableRow>}
          {rows.map((r) => (
            <Tr key={r.id}>
              <Td><Badge tone="brand">{label(r.originalTable)}</Badge></Td>
              {/* Số chứng từ thật, không phải 8 ký tự đầu của UUID: người dùng
                  vào đây để tìm "PI 000378 đâu rồi". */}
              <Td className="font-mono tabular text-xs font-medium text-ink">
                {r.documentNo ?? <span className="text-ink-faint">{r.originalId.slice(0, 8)}…</span>}
              </Td>
              <Td className="text-xs text-ink-soft">{r.partyName ?? "—"}</Td>
              <Td className="font-mono tabular text-xs text-ink-soft">{r.customerPoNo ?? "—"}</Td>
              <Td align="right" className="font-mono tabular text-ink">
                {r.totalAmount != null ? (
                  formatMoney(r.totalAmount, r.currency ?? "USD")
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </Td>
              <Td className="text-ink-soft">{r.deletedByName ?? "—"}</Td>
              {/* Tương đối để đọc nhanh, TUYỆT ĐỐI khi rê chuột: đây là vết
                  kiểm toán, "3 ngày trước" không dùng để đối chiếu được. */}
              <Td className="font-mono tabular text-xs text-ink-soft">
                <span title={formatDateTime(r.deletedAt, i18n.language)}>
                  {timeAgo(r.deletedAt, i18n.language)}
                </span>
              </Td>
              <Td align="right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("archive.viewSnapshot")}
                    title={t("archive.viewSnapshot")}
                    onClick={() => setViewing(r.id)}
                  >
                    <Eye className="size-3.5" />
                  </Button>
                  {/* Bảng xoá cứng vẫn được lưu trữ để có vết kiểm toán nhưng
                      không dựng lại được. Hiện nút rồi báo lỗi 422 là hứa suông;
                      máy chủ nói rõ qua cờ `restorable`. */}
                  {r.restorable ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={restore.isPending}
                      onClick={() =>
                        setConfirming({
                          id: r.id,
                          label: r.documentNo ?? r.partyName ?? label(r.originalTable),
                        })
                      }
                    >
                      <RotateCcw className="size-3.5" /> {t("common.restore")}
                    </Button>
                  ) : (
                    <span className="text-2xs text-ink-faint" title={t(r.originalTable.startsWith("manufacturing_")?"production.archiveHint":"archive.notRestorableHint")}>
                      {t(r.originalTable.startsWith("manufacturing_")?"production.Archived":"archive.notRestorable")}
                    </span>
                  )}
                </div>
              </Td>
            </Tr>
          ))}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
